import { create } from 'zustand';
import {
  kanbanService,
  buildBoardFiltersFromUi,
} from '../services/kanbanService';
import {
  KanbanTask,
  TaskStatus,
  CreateKanbanTaskRequest,
  UpdateKanbanTaskRequest,
  TaskUserRef,
  TaskTypeRef,
  TaskLabelRef,
  BoardFilters,
} from '../types/kanban';
import { ApiError } from '../types/api';

interface KanbanState {
  tasks: KanbanTask[];
  columnCounts: Record<TaskStatus, number>;
  filters: BoardFilters;
  assignees: TaskUserRef[];
  reporters: TaskUserRef[];
  taskTypes: TaskTypeRef[];
  taskLabels: TaskLabelRef[];
  labelPalette: string[];
  isLoading: boolean;
  error: ApiError | null;

  fetchLookups: () => Promise<void>;
  createTaskLabel: (name: string, colorHex: string) => Promise<TaskLabelRef>;
  updateTaskLabel: (
    id: number,
    data: { name?: string; colorHex?: string }
  ) => Promise<TaskLabelRef>;
  deleteTaskLabel: (id: number) => Promise<void>;
  fetchBoard: (uiFilters?: {
    search?: string;
    status?: string;
    assignee?: string;
    priority?: string;
    label?: string;
  }) => Promise<void>;
  moveTask: (taskId: string, targetStatus: TaskStatus) => Promise<void>;
  createTask: (data: CreateKanbanTaskRequest) => Promise<void>;
  updateTask: (id: string, data: UpdateKanbanTaskRequest) => Promise<void>;
  clearError: () => void;
}

const emptyCounts = (): Record<TaskStatus, number> => ({
  un_assigned: 0,
  todo: 0,
  waiting_for_info: 0,
  in_progress: 0,
  completed: 0,
});

export const useKanbanStore = create<KanbanState>()((set, get) => ({
  tasks: [],
  columnCounts: emptyCounts(),
  filters: {},
  assignees: [],
  reporters: [],
  taskTypes: [],
  taskLabels: [],
  labelPalette: [],
  isLoading: false,
  error: null,

  fetchLookups: async () => {
    try {
      const [assignees, reporters, taskTypes, taskLabels, labelPalette] =
        await Promise.all([
          kanbanService.getAssignees(),
          kanbanService.getReporters(),
          kanbanService.getTaskTypes(),
          kanbanService.getTaskLabels(),
          kanbanService.getLabelPalette(),
        ]);
      set({ assignees, reporters, taskTypes, taskLabels, labelPalette });
    } catch {
      // lookups optional for board display
    }
  },

  createTaskLabel: async (name, colorHex) => {
    const created = await kanbanService.createTaskLabel({ name, colorHex });
    set((state) => ({
      taskLabels: [...state.taskLabels, created].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    }));
    return created;
  },

  updateTaskLabel: async (id, data) => {
    const updated = await kanbanService.updateTaskLabel(String(id), data);
    set((state) => ({
      taskLabels: state.taskLabels
        .map((l) => (l.labelId === id ? updated : l))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
    return updated;
  },

  deleteTaskLabel: async (id) => {
    await kanbanService.deleteTaskLabel(String(id));
    set((state) => ({
      taskLabels: state.taskLabels.filter((l) => l.labelId !== id),
    }));
  },

  fetchBoard: async (uiFilters) => {
    const filters = uiFilters ? buildBoardFiltersFromUi(uiFilters) : get().filters;
    set({ isLoading: true, error: null, filters });
    try {
      let columns = await kanbanService.fetchBoard(filters);

      if (uiFilters?.assignee === 'unassigned') {
        columns = columns.map((col) => ({
          ...col,
          tasks: col.tasks.filter((t) => !t.assignee),
          count: col.tasks.filter((t) => !t.assignee).length,
        }));
      }

      const tasks = kanbanService.boardToKanbanTasks(columns);
      const columnCounts = emptyCounts();
      columns.forEach((col) => {
        columnCounts[col.status] = col.count;
      });

      set({ tasks, columnCounts, isLoading: false });
    } catch (error) {
      set({ isLoading: false, error: error as ApiError });
      throw error;
    }
  },

  moveTask: async (taskId, targetStatus) => {
    const prevTasks = get().tasks;
    set({
      tasks: prevTasks.map((t) =>
        t.taskId === taskId
          ? {
              ...t,
              columnId: targetStatus,
              isClosed: targetStatus === 'completed',
            }
          : t
      ),
    });
    try {
      await kanbanService.updateTaskStatus(taskId, targetStatus);
      await get().fetchBoard();
    } catch (error) {
      set({ tasks: prevTasks, error: error as ApiError });
      throw error;
    }
  },

  createTask: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await kanbanService.createTask(data);
      await get().fetchBoard();
    } catch (error) {
      set({ isLoading: false, error: error as ApiError });
      throw error;
    }
  },

  updateTask: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      await kanbanService.updateTask(id, data);
      await get().fetchBoard();
    } catch (error) {
      set({ isLoading: false, error: error as ApiError });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
